export const accessControlStatus = {
  allowed: "Allowed",
  restricted: "Restricted",
  trainingRequired: "Training Required",
  unknown: "Unknown",
};

const MULTI_VALUE_FIELDS = new Set(["bookable_for_users"]);
const FIELD_ALIASES = {
  is_booking_controlled: ["isBookingControlled"],
  bookable_for_users: ["bookableForUsers"],
  cidm_group_name: ["cidmGroupName"],
  cidm_group_cidm_url: ["cidmGroupCidmUrl"],
  cidm_group_more_info_url: ["cidmGroupMoreInfoUrl"],
};

const isValueObject = (value) => {
  return Boolean(value) && typeof value === "object" && Object.prototype.hasOwnProperty.call(value, "value");
};

const toArrayValue = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (isValueObject(value)) {
    if (Array.isArray(value.value)) {
      return value.value;
    }

    return value.value ? [value.value] : [];
  }

  return typeof value === "string" && value.length > 0 ? [value] : [];
};

const toSingleValue = (value, defaultValue) => {
  if (Array.isArray(value)) {
    return value.length > 0 ? value[0] : defaultValue;
  }

  if (isValueObject(value)) {
    return value.value;
  }

  return value;
};

const readAccessControlValue = (value, shouldReturnArray, defaultValue) => {
  if (shouldReturnArray) {
    return toArrayValue(value);
  }

  return toSingleValue(value, defaultValue);
};

const resolveAccessControlValue = (source, key, shouldReturnArray, defaultValue) => {
  if (!source || !Object.prototype.hasOwnProperty.call(source, key)) {
    return undefined;
  }

  const resolved = readAccessControlValue(source[key], shouldReturnArray, defaultValue);
  return resolved ?? defaultValue;
};

export const getAccessControlFieldValue = (data, key, defaultValue = undefined) => {
  if (!data || !key) {
    return defaultValue;
  }

  const shouldReturnArray = MULTI_VALUE_FIELDS.has(key);

  const candidateKeys = [key, ...(FIELD_ALIASES[key] || [])];

  // The booking-control flags arrive in different shapes depending on the caller:
  // flattened on the instrument (equipment list), under `fields` (CloudSearch), or
  // still nested under `addInfo` (raw GraphQL instrument used by the booking popup).
  const candidateSources = [data, data.fields, data.addInfo];

  for (const candidateKey of candidateKeys) {
    for (const source of candidateSources) {
      const resolved = resolveAccessControlValue(source, candidateKey, shouldReturnArray, defaultValue);
      if (resolved !== undefined) {
        return resolved;
      }
    }
  }

  return defaultValue;
};

const normalizeCidmGroups = (cidmGroups = []) => {
  if (!Array.isArray(cidmGroups)) {
    return [];
  }

  return cidmGroups
    .filter((value) => value !== undefined && value !== null)
    .map((value) => String(value).toLowerCase());
};

const normalizeBookableUsers = (bookableForUsers) => {
  if (Array.isArray(bookableForUsers)) {
    return bookableForUsers;
  }

  if (bookableForUsers === undefined || bookableForUsers === null) {
    return [];
  }

  return [bookableForUsers];
};

const hasCidmGroupAccess = (cidmGroupName, userCidmGroups) => {
  if (!cidmGroupName) {
    return false;
  }

  const normalizedCidmGroupName = cidmGroupName.toLowerCase();
  return userCidmGroups.some((userCidmGroup) => normalizedCidmGroupName === userCidmGroup);
};

const hasDirectBookingAccess = (bookableForUsers, storeUser) => {
  return bookableForUsers.some((user) => user === storeUser?.id || user === storeUser?.user);
};

const isTrueAccessControlValue = (value) => {
  return value === true || (typeof value === "string" && value.trim().toLowerCase() === "true");
};

const getUserAccessState = ({ cidmGroupName, userCidmGroups, bookableForUsers, storeUser }) => {
  if (cidmGroupName) {
    return hasCidmGroupAccess(cidmGroupName, userCidmGroups);
  }

  return hasDirectBookingAccess(bookableForUsers, storeUser);
};

const resolveAccessControlStatus = ({ isBookingControlled, userHasAccess, cidmGroupName }) => {
  if (!isBookingControlled) {
    return accessControlStatus.allowed;
  }

  if (userHasAccess) {
    return accessControlStatus.allowed;
  }

  if (cidmGroupName) {
    return accessControlStatus.trainingRequired;
  }

  return accessControlStatus.restricted;
};

export const getBookingAccessControlState = (data, storeUser = {}) => {
  const cidmGroupName = getAccessControlFieldValue(data, "cidm_group_name");
  const isBookingControlled =
    isTrueAccessControlValue(getAccessControlFieldValue(data, "is_booking_controlled")) ||
    Boolean(cidmGroupName);
  const bookableForUsers = normalizeBookableUsers(getAccessControlFieldValue(data, "bookable_for_users"));
  const userCidmGroups = normalizeCidmGroups(storeUser?.cidmGroups);
  const userHasAccess = getUserAccessState({ cidmGroupName, userCidmGroups, bookableForUsers, storeUser });
  const status = resolveAccessControlStatus({ isBookingControlled, userHasAccess, cidmGroupName });
  return {
    status,
    cidmGroupName,
    isBookingControlled,
    bookableForUsers,
    userHasAccess,
  };
};

export const isAllowedAccessControlStatus = (data, storeUser = {}) => {
  return getBookingAccessControlState(data, storeUser).status === accessControlStatus.allowed;
};

// CIDM identity for an equipment comes exclusively from cidmGroup.groupName across
// every booking-access config item, mirroring the backend's isUserAllowedToBook
// contract (booking-group names like "Radian_GroupB" are NOT CIDM groups and must
// not be used to grant CIDM access). Returns {} when no CIDM group is linked so
// callers can distinguish "not controlled by CIDM" from "enrichment not loaded yet".
export const extractCidmAccessControlFromConfigItems = (items = []) => {
  for (const item of items || []) {
    const groups = item?.groups?.items || [];

    for (const groupItem of groups) {
      const cidmGroup = groupItem?.cidmGroup;
      if (cidmGroup?.groupName) {
        return {
          cidmGroupName: cidmGroup.groupName,
          cidmGroupCidmUrl: cidmGroup.cidmUrl,
          cidmGroupMoreInfoUrl: cidmGroup.moreInfoUrl,
        };
      }
    }
  }

  return {};
};
